import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Input, Textarea } from '@/components/ui';
import { moveDeal, updateDeal } from '@/data/client';
import type { Deal, Pipeline } from '@/data/types';
import { money } from '@/lib/format';
import { FormError, Modal } from './Modal';
import { OptionSelect } from './OptionSelect';

type Stage = Pipeline['stages'][number];

/* Every stage move — drag on the board or the stage picker on the deal page —
   goes through here, so the won/lost rules have one UI. Open stages move
   immediately; Won confirms the money (the server refuses a won deal with no
   value); Lost asks for a reason from the shared list. */
export function useDealMover(onMoved?: (deal: Deal) => void): { request: (deal: Deal, stage: Stage) => void; ui: ReactNode; error: unknown } {
  const qc = useQueryClient();
  const [pending, setPending] = useState<{ deal: Deal; stage: Stage } | null>(null);
  const refresh = (deal: Deal) => {
    for (const k of ['deals', 'deal', 'company', 'companies']) void qc.invalidateQueries({ queryKey: [k] });
    onMoved?.(deal);
  };
  const quick = useMutation({
    mutationFn: ({ deal, stage }: { deal: Deal; stage: Stage }) => moveDeal(deal.id, { stageId: stage.id }),
    onSuccess: refresh,
  });

  const request = (deal: Deal, stage: Stage) => {
    if (stage.id === deal.stageId) return;
    quick.reset();
    if (stage.kind === 'open') quick.mutate({ deal, stage });
    else setPending({ deal, stage });
  };

  let ui: ReactNode = null;
  if (pending?.stage.kind === 'won') ui = <WonModal deal={pending.deal} stage={pending.stage} onClose={() => setPending(null)} onDone={refresh} />;
  if (pending?.stage.kind === 'lost') ui = <LostModal deal={pending.deal} stage={pending.stage} onClose={() => setPending(null)} onDone={refresh} />;
  return { request, ui, error: quick.error };
}

function WonModal({ deal, stage, onClose, onDone }: { deal: Deal; stage: Stage; onClose: () => void; onDone: (d: Deal) => void }) {
  const [mrr, setMrr] = useState(deal.mrr?.toString() ?? '');
  const [setupFee, setSetupFee] = useState(deal.setupFee?.toString() ?? '');
  const [months, setMonths] = useState(deal.contractMonths?.toString() ?? '');
  const save = useMutation({
    mutationFn: async () => {
      await updateDeal(deal.id, { mrr: mrr || null, setupFee: setupFee || null, contractMonths: months || null });
      return moveDeal(deal.id, { stageId: stage.id });
    },
    onSuccess: (d) => { onDone(d); onClose(); },
  });
  return (
    <Modal title={`Mark “${deal.name}” won`} onClose={onClose} footer={<>
      <Button variant="ghost" onClick={onClose}>Cancel</Button>
      <Button onClick={() => save.mutate()} disabled={save.isPending || (!mrr && !setupFee)}>{save.isPending ? 'Saving…' : 'Mark won'}</Button>
    </>}>
      <p className="crm-cell-muted">
        Confirm what was signed. This locks the deal's attribution{deal.source ? '' : ' (it has no lead, so it will report as unattributed)'}.
        {deal.strategyFeeAmount ? ` The ${money(deal.strategyFeeAmount)} strategy fee is credited toward the engagement, not added to it.` : ''}
      </p>
      <div className="crm-grid2">
        <Input label="Monthly retainer (MRR)" inputMode="decimal" placeholder="$" autoFocus value={mrr} onChange={(e) => setMrr(e.target.value)} />
        <Input label="Setup fee (month 1)" inputMode="decimal" placeholder="$" value={setupFee} onChange={(e) => setSetupFee(e.target.value)} />
        <Input label="Term (months)" inputMode="numeric" placeholder="Month to month" value={months} onChange={(e) => setMonths(e.target.value)} />
      </div>
      <FormError error={save.error} />
    </Modal>
  );
}

function LostModal({ deal, stage, onClose, onDone }: { deal: Deal; stage: Stage; onClose: () => void; onDone: (d: Deal) => void }) {
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const save = useMutation({
    mutationFn: () => moveDeal(deal.id, { stageId: stage.id, lostReasonId: reason, lostNote: note || undefined }),
    onSuccess: (d) => { onDone(d); onClose(); },
  });
  return (
    <Modal title={`Mark “${deal.name}” lost`} onClose={onClose} footer={<>
      <Button variant="ghost" onClick={onClose}>Cancel</Button>
      <Button variant="danger" onClick={() => save.mutate()} disabled={save.isPending || !reason}>{save.isPending ? 'Saving…' : 'Mark lost'}</Button>
    </>}>
      <OptionSelect kind="lost_reason" label="Reason" required value={reason} onChange={setReason} />
      <Textarea label="Note (optional)" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      {deal.strategyFeeAmount ? <p className="crm-cell-muted">If the {money(deal.strategyFeeAmount)} strategy fee was paid, it is due back as a refund.</p> : null}
      <FormError error={save.error} />
    </Modal>
  );
}
